import { useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useSC, Drawer, Card, Pill, SCEN_TONE, Table, Td, money, fmtDate, Loading } from "./parts";

function Info({ k, v }) {
  return <div><div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>{k}</div><div className="text-sm font-semibold">{v || "—"}</div></div>;
}

function PlanRecord({ p, onOpenPlan }) {
  const [day, setDay] = useState(0);
  if (!p) return <Card title="Diet plan"><p className="text-sm" style={{ color: "var(--text-secondary)" }}>No diet plan assigned yet. First consultation pending.</p></Card>;
  const d = p.days?.[day];
  return (
    <Card title={`Diet plan · ${p.id}`} right={<button data-testid="sc-open-plan" onClick={() => onOpenPlan(p.template_id, p.template)} className="text-xs font-bold underline" style={{ color: "var(--primary)" }}>View plan record</button>}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Info k="Template" v={p.template} /><Info k="Status" v={p.status} />
        <Info k="Assigned" v={fmtDate(p.assigned)} /><Info k="Delivered" v={p.delivered ? fmtDate(p.delivered) : "Pending"} />
      </div>
      {p.days && (
        <>
          <div className="flex gap-1 flex-wrap mb-3">
            {p.days.map((_, i) => (
              <button key={i} data-testid={`sc-plan-day-${i}`} onClick={() => setDay(i)} className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: i === day ? "var(--primary)" : "transparent", color: i === day ? "var(--yellow)" : "var(--primary)", border: "1.5px solid var(--primary)" }}>Day {i + 1}</button>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {d?.meals?.map((m, i) => (
              <div key={i} className="rounded-lg p-3 text-sm" style={{ background: "var(--bg)" }}>
                <div className="text-xs font-bold" style={{ color: "var(--text-secondary)" }}>{m.time} · {m.name}</div>
                <ul className="list-disc pl-4 mt-1">{m.items.map((it, j) => <li key={j}>{it}</li>)}</ul>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

export function ClientProfile({ cid, onClose, onOpenDietitian, onOpenPlan }) {
  const { data: c, loading } = useSC(`/clients/${cid}`);
  return (
    <Drawer onClose={onClose} testid="sc-client-profile">
      {loading || !c ? <Loading /> : (
        <div className="space-y-4">
          <div>
            <div className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>{c.cid}</div>
            <h2 className="font-display text-3xl" style={{ color: "var(--primary)" }}>{c.name}</h2>
            <div className="flex gap-2 mt-2 flex-wrap"><Pill tone={SCEN_TONE[c.scenario]}>{c.plan_status}</Pill><Pill tone="grey">{c.sub_status.replace("_", " ")}</Pill></div>
          </div>
          <Card>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Info k="Age / gender" v={`${c.age} · ${c.gender}`} /><Info k="City" v={c.city} /><Info k="Phone" v={c.phone} /><Info k="Goal" v={c.goal} />
              <div><div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>Dietitian</div>
                <button data-testid="sc-client-dietitian-link" onClick={() => onOpenDietitian(c.dietitian_id)} className="text-sm font-semibold underline" style={{ color: "var(--primary)" }}>{c.dietitian}</button></div>
              <Info k="Joined" v={fmtDate(c.joined)} /><Info k="Package" v={c.package} /><Info k="Current period" v={`${fmtDate(c.period_start)} → ${fmtDate(c.period_end)}`} />
              <Info k="Renewals" v={c.renewals} /><Info k="Sales rep" v={c.sales_rep} /><Info k="Next follow-up" v={fmtDate(c.followups.next) + (c.followups.overdue ? " (overdue)" : "")} />
              <Info k="Yoga batch" v={c.yoga ? `${c.yoga.batch} · ${c.yoga.style} · ${c.yoga.slot} · ${c.yoga.coach}` : "Not enrolled"} />
            </div>
          </Card>
          <PlanRecord p={c.plan_record} onOpenPlan={onOpenPlan} />
          <Card title={`Progress · ${c.start_w} kg → ${c.cur_w} kg (target ${c.target_w} kg)`}>
            <div className="h-48"><ResponsiveContainer><LineChart data={c.progress}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(20,20,43,0.08)" /><XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} /><YAxis domain={["auto", "auto"]} fontSize={11} /><Tooltip />
              <Line dataKey="weight" stroke="#B93B3B" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart></ResponsiveContainer></div>
          </Card>
          <div className="grid md:grid-cols-2 gap-4">
            <Card title={`Consultations (${c.total_consults})`}>
              <Table head={["Date", "Topic", "Mode"]}>{c.consultations.map((h, i) => <tr key={i}><Td>{fmtDate(h.date)}</Td><Td>{h.topic}</Td><Td>{h.mode}</Td></tr>)}</Table>
            </Card>
            <Card title="Payments & renewals">
              <Table head={["Txn", "Date", "Type", "Amount"]}>{c.payments.map((p) => <tr key={p.id}><Td className="font-mono text-xs">{p.id}</Td><Td>{fmtDate(p.date)}</Td><Td className="capitalize">{p.type}</Td><Td className="font-semibold">{money(p.amount)}</Td></tr>)}</Table>
            </Card>
          </div>
        </div>
      )}
    </Drawer>
  );
}
